import { useEffect } from "react";
import { supabase } from "./lib/supabase";

type ProcedureLink={
 id:string;
 document_number:string;
 document_type:string;
 parent_wps_id:string|null;
 process:string|null;
 revision:string|null;
 status:string|null;
};

const HIERARCHY_CLASS="project-procedure-hierarchy";
const norm=(value:string|null|undefined)=>(value||"").trim();
const processParts=(value:string|null|undefined)=>norm(value).split(/[\\/,;+&]/).map(x=>x.trim().toUpperCase()).filter(Boolean);

export default function ProjectProcedureHierarchy(){
 useEffect(()=>{
  let stopped=false;
  let busy=false;
  let timer:number|undefined;

  const schedule=()=>{
   window.clearTimeout(timer);
   timer=window.setTimeout(()=>void build(),120);
  };

  const currentProject=async()=>{
   const projectHead=document.querySelector(".project-detail-head");
   const projectName=projectHead?.querySelector("h1")?.textContent?.trim()||"";
   const projectNumber=projectHead?.querySelector(".eyebrow")?.textContent?.trim()||"";
   let query=supabase.from("projects").select("id,company_id").limit(1);
   if(projectNumber&&projectNumber!=="PROJECT")query=query.eq("project_number",projectNumber);
   else query=query.eq("name",projectName);
   const {data}=await query.maybeSingle();
   return data as {id:string;company_id:string}|null;
  };

  const build=async()=>{
   if(stopped||busy)return;
   const section=[...document.querySelectorAll<HTMLElement>("section.table-card")].find(x=>x.querySelector(".section-title .eyebrow")?.textContent?.trim()==="PROJECT PROCEDURES");
   const list=section?.querySelector<HTMLElement>(".record-list");
   if(!section||!list)return;

   const sourceArticles=[...list.querySelectorAll<HTMLElement>(":scope > article:not([data-hierarchy-clone])")];
   if(!sourceArticles.length){section.querySelector(`.${HIERARCHY_CLASS}`)?.remove();return;}

   const numbers=sourceArticles.map(article=>article.querySelector("div b")?.textContent?.trim()).filter(Boolean) as string[];
   if(!numbers.length)return;

   busy=true;
   try{
    const {data,error}=await supabase.from("procedures").select("id,document_number,document_type,parent_wps_id,process,revision,status").in("document_number",numbers);
    if(stopped||error)return;

    const rows=(data||[]) as ProcedureLink[];
    const wpsRows=rows.filter(row=>row.document_type==="WPS");
    const childRows=rows.filter(row=>row.document_type==="WPDS");
    const signature=[...rows].sort((a,b)=>a.document_number.localeCompare(b.document_number)).map(row=>`${row.id}:${row.parent_wps_id||"none"}:${row.status||""}`).join("|");
    const existing=section.querySelector<HTMLElement>(`.${HIERARCHY_CLASS}`);
    if(existing?.dataset.signature===signature)return;

    existing?.remove();
    sourceArticles.forEach(article=>{article.style.display="none";article.dataset.hierarchySource="true"});

    const hierarchy=document.createElement("div");
    hierarchy.className=HIERARCHY_CLASS;
    hierarchy.dataset.signature=signature;
    const articleByNumber=new Map<string,HTMLElement>();
    for(const article of sourceArticles){const number=article.querySelector("div b")?.textContent?.trim();if(number)articleByNumber.set(number,article);}

    const groupedChildren=new Set<string>();
    const orderedWps=[...wpsRows].sort((a,b)=>`${norm(a.process)} ${a.document_number}`.localeCompare(`${norm(b.process)} ${b.document_number}`));
    for(const wps of orderedWps){
     const parentArticle=articleByNumber.get(wps.document_number);if(!parentArticle)continue;
     const children=childRows.filter(child=>child.parent_wps_id===wps.id).sort((a,b)=>a.document_number.localeCompare(b.document_number));
     children.forEach(child=>groupedChildren.add(child.id));
     hierarchy.appendChild(makeGroup(parentArticle,wps,children.map(child=>({row:child,article:articleByNumber.get(child.document_number)})).filter(x=>x.article) as {row:ProcedureLink;article:HTMLElement}[]));
    }

    const orphanChildren=childRows.filter(child=>!groupedChildren.has(child.id));
    if(orphanChildren.length){
     const orphan=document.createElement("section");orphan.className="procedure-hierarchy-orphans";
     const head=document.createElement("div");head.className="procedure-hierarchy-orphan-head";
     head.innerHTML=`<div><span class="eyebrow">UNASSIGNED WPDS</span><b>${orphanChildren.length} WPDS ${orphanChildren.length===1?"needs":"need"} a parent WPS</b><small>Use Link WPDS on the correct WPS above to place each data sheet in the right procedure group.</small></div><span class="procedure-hierarchy-warning">Needs review</span>`;
     orphan.appendChild(head);
     const body=document.createElement("div");body.className="procedure-hierarchy-orphan-list";
     for(const child of orphanChildren){const article=articleByNumber.get(child.document_number);if(article)body.appendChild(cloneArticle(article,"orphan"));}
     orphan.appendChild(body);hierarchy.appendChild(orphan);
    }
    list.insertBefore(hierarchy,list.firstChild);
   }finally{busy=false}
  };

  const makeGroup=(wpsArticle:HTMLElement,wps:ProcedureLink,children:{row:ProcedureLink;article:HTMLElement}[])=>{
   const group=document.createElement("section");group.className="procedure-hierarchy-group";
   const head=document.createElement("div");head.className="procedure-hierarchy-head";
   const parent=cloneArticle(wpsArticle,"parent");parent.dataset.wpsId=wps.id;
   const relationship=document.createElement("div");relationship.className="procedure-hierarchy-meta";
   relationship.innerHTML=`<span>${children.length} WPDS${children.length===1?"":"s"}</span>`;
   const actions=document.createElement("div");actions.className="procedure-hierarchy-actions";
   const addButton=document.createElement("button");addButton.type="button";addButton.className="procedure-hierarchy-link";addButton.textContent="＋ Link WPDS";
   const toggle=document.createElement("button");toggle.type="button";toggle.className="procedure-hierarchy-toggle";toggle.setAttribute("aria-expanded","true");toggle.textContent=children.length?"Hide WPDS":"No WPDS";toggle.disabled=!children.length;
   actions.append(addButton,toggle);head.append(parent,relationship,actions);group.appendChild(head);

   const linkBox=document.createElement("div");linkBox.className="procedure-hierarchy-linkbox";linkBox.hidden=true;group.appendChild(linkBox);

   addButton.addEventListener("click",async event=>{
    event.stopPropagation();
    if(!linkBox.hidden){linkBox.hidden=true;addButton.textContent="＋ Link WPDS";return;}
    addButton.disabled=true;addButton.textContent="Loading library…";
    const project=await currentProject();
    if(!project){addButton.disabled=false;addButton.textContent="＋ Link WPDS";return;}

    const {data,error}=await supabase.from("procedures").select("id,document_number,document_type,parent_wps_id,process,revision,status").eq("company_id",project.company_id).eq("document_type","WPDS").eq("status","active").order("document_number");
    const allWpds=((data||[]) as ProcedureLink[]);
    const wpsProcesses=new Set(processParts(wps.process));
    const matching=allWpds.filter(item=>{const parts=processParts(item.process);return !wpsProcesses.size||!parts.length||parts.some(part=>wpsProcesses.has(part));});

    const parentIds=[...new Set(matching.map(item=>item.parent_wps_id).filter(Boolean))] as string[];
    const parentNames=new Map<string,string>();
    if(parentIds.length){
     const {data:parents}=await supabase.from("procedures").select("id,document_number").in("id",parentIds);
     for(const p of parents||[])parentNames.set(String(p.id),String(p.document_number));
    }

    linkBox.innerHTML="";
    const label=document.createElement("div");label.className="procedure-hierarchy-link-title";
    label.innerHTML=`<b>WPDS Library · ${wps.document_number}</b><small>${error?"Could not load the WPDS library.":`${matching.length} active ${norm(wps.process)||"matching"} WPDS record${matching.length===1?"":"s"} found in the company library.`}</small>`;
    const controls=document.createElement("div");controls.className="procedure-hierarchy-link-controls";
    const select=document.createElement("select");select.innerHTML='<option value="">Choose WPDS from library…</option>';
    for(const item of matching){
     const option=document.createElement("option");option.value=item.id;
     if(item.parent_wps_id===wps.id){option.disabled=true;option.textContent=`${item.document_number} · ${item.process||"Process not entered"} · already linked here`;}
     else if(item.parent_wps_id){option.textContent=`${item.document_number} · ${item.process||"Process not entered"} · currently under ${parentNames.get(item.parent_wps_id)||"another WPS"}`;}
     else option.textContent=`${item.document_number} · ${item.process||"Process not entered"} · unassigned`;
     select.appendChild(option);
    }
    const link=document.createElement("button");link.type="button";link.className="primary";link.textContent="Link under this WPS";link.disabled=true;
    select.addEventListener("change",()=>{link.disabled=!select.value});controls.append(select,link);linkBox.append(label,controls);
    if(!matching.length){const empty=document.createElement("small");empty.className="procedure-hierarchy-link-empty";empty.textContent="No active WPDS records in the company library match this WPS process.";linkBox.appendChild(empty);}

    link.addEventListener("click",async e=>{
     e.stopPropagation();const wpdsId=select.value;if(!wpdsId)return;
     link.disabled=true;link.textContent="Linking…";
     const {data:{user}}=await supabase.auth.getUser();if(!user){link.textContent="Link under this WPS";link.disabled=false;return;}
     const [{error:updateError},{error:projectLinkError}]=await Promise.all([
      supabase.from("procedures").update({parent_wps_id:wps.id}).eq("id",wpdsId),
      supabase.from("project_procedures").upsert([{company_id:project.company_id,project_id:project.id,procedure_id:wps.id,created_by:user.id},{company_id:project.company_id,project_id:project.id,procedure_id:wpdsId,created_by:user.id}],{onConflict:"project_id,procedure_id"})
     ]);
     if(updateError||projectLinkError){link.textContent="Try again";link.disabled=false;return;}
     linkBox.hidden=true;addButton.textContent="＋ Link WPDS";addButton.disabled=false;
     const nativeList=group.closest(".record-list");
     if(nativeList){const {data:record}=await supabase.from("procedures").select("id,document_number,document_type,process,status,revision").eq("id",wpdsId).maybeSingle();if(record&&!Array.from(nativeList.querySelectorAll("article div b")).some(x=>x.textContent?.trim()===record.document_number)){const article=document.createElement("article");article.innerHTML=`<span class="process">WPDS</span><div><b>${record.document_number}</b><small>${record.process} · ${record.revision||"No revision"}</small></div><span class="status active">● ACTIVE</span>`;nativeList.appendChild(article);}}
     window.dispatchEvent(new Event("arcvault:project-procedure-changed"));schedule();
    });
    linkBox.hidden=false;addButton.disabled=false;addButton.textContent="Close library";
   });

   const childBox=document.createElement("div");childBox.className="procedure-hierarchy-children";
   if(children.length){for(const {article} of children){const wrap=document.createElement("div");wrap.className="procedure-hierarchy-child-row";const connector=document.createElement("span");connector.className="procedure-hierarchy-connector";connector.textContent="↳";const child=cloneArticle(article,"child");child.dataset.parentWps=wps.document_number;wrap.append(connector,child);childBox.appendChild(wrap);}}
   else{const empty=document.createElement("div");empty.className="procedure-hierarchy-empty";empty.textContent="No WPDS linked under this WPS yet. Use Link WPDS above.";childBox.appendChild(empty);}
   group.appendChild(childBox);
   toggle.addEventListener("click",event=>{event.stopPropagation();if(!children.length)return;const collapsed=group.classList.toggle("collapsed");toggle.setAttribute("aria-expanded",String(!collapsed));toggle.textContent=collapsed?`Show ${children.length} WPDS${children.length===1?"":"s"}`:"Hide WPDS";});
   return group;
  };

  const cloneArticle=(article:HTMLElement,kind:string)=>{const clone=article.cloneNode(true) as HTMLElement;clone.style.display="";clone.removeAttribute("data-hierarchy-source");clone.dataset.hierarchyClone=kind;clone.classList.add(`procedure-hierarchy-${kind}`);return clone;};
  const observer=new MutationObserver(mutations=>{if(mutations.some(m=>[...m.addedNodes,...m.removedNodes].some(node=>node instanceof HTMLElement&&!node.closest?.(`.${HIERARCHY_CLASS}`))))schedule();});
  observer.observe(document.body,{childList:true,subtree:true});
  const onChanged=()=>schedule();window.addEventListener("arcvault:project-procedure-changed",onChanged);schedule();
  return()=>{stopped=true;window.clearTimeout(timer);observer.disconnect();window.removeEventListener("arcvault:project-procedure-changed",onChanged)};
 },[]);
 return null;
}
